import { useAuth0 } from '@auth0/auth0-vue';
import { watch } from 'vue';
import logger from '@/utils/logger';
import { getUserRoles, hasAdminAccess as checkAdminAccess, hasSuperAdminAccess } from '@/utils/auth';

/**
 * Auth guard for protecting routes that require authentication and admin/super-admin role
 * @param {Object} to - Route the user is navigating to
 * @param {Object} from - Route the user is navigating from
 * @param {Function} next - Function to resolve the navigation
 */
export const authGuard = async (to, from, next) => {
  const auth0 = useAuth0();
  const authStart = Date.now();
  
  logger.auth('auth_guard_start', {
    targetRoute: to.name,
    targetPath: to.path,
    isLoading: auth0.isLoading.value
  });
  
  // Create a promise that resolves when Auth0 is done loading
  const waitForAuth = () => {
    return new Promise((resolve) => {
      // If already loaded, resolve immediately
      if (!auth0.isLoading.value) {
        logger.debug('Auth0 already loaded');
        resolve();
      } else {
        logger.debug('Waiting for Auth0 to load...');
        // Otherwise, watch for loading to complete
        const unwatch = watch(
          () => auth0.isLoading.value,
          (loading) => {
            if (!loading) {
              logger.debug('Auth0 loading completed');
              unwatch();
              resolve();
            }
          }
        );
      }
    });
  };
  
  try {
    // Wait for Auth0 to finish loading
    await waitForAuth();
    
    // Check if the user is authenticated
    const isAuthenticated = auth0.isAuthenticated.value;
    const authDuration = Date.now() - authStart;
    
    logger.auth('auth_check_complete', {
      isAuthenticated,
      duration: authDuration,
      targetRoute: to.name,
      targetPath: to.path
    });
    
    if (isAuthenticated) {
      // Log user information (sanitized)
      const user = auth0.user.value;
      logger.auth('user_authenticated', {
        userId: user?.sub,
        email: user?.email ? `${user.email.charAt(0)}***@${user.email.split('@')[1]}` : null,
        nickname: user?.nickname,
        targetRoute: to.name
      });
      
      // Check if user has admin or super-admin role
      const roles = getUserRoles(user);
      const hasAdminAccess = checkAdminAccess(user);
      const hasRequiredAccess = to.meta.requiresSuperAdmin
        ? hasSuperAdminAccess(user)
        : hasAdminAccess;
      
      logger.auth('role_check', {
        userEmail: user?.email,
        roles,
        hasAdminAccess,
        requiresSuperAdmin: !!to.meta.requiresSuperAdmin,
        hasRequiredAccess,
        targetRoute: to.name
      });
      
      if (hasRequiredAccess) {
        // If authenticated and has admin/super-admin role, proceed to the route
        logger.auth('admin_access_granted', {
          userEmail: user?.email,
          roles,
          targetRoute: to.name
        });
        return next();
      } else {
        // If authenticated but doesn't have admin/super-admin role, redirect to access denied
        logger.auth('admin_access_denied', {
          userEmail: user?.email,
          roles,
          targetRoute: to.name,
          reason: 'insufficient_role'
        });
        
        // Redirect to access denied page
        return next('/access-denied');
      }
    } else {
      // If not authenticated and trying to access a protected route,
      // redirect to login page
      if (to.path !== '/login') {
        logger.auth('redirecting_to_login', {
          originalDestination: to.fullPath,
          reason: 'not_authenticated'
        });
        
        // Store the original destination to redirect after login
        const appState = {
          targetUrl: to.fullPath
        };
        
        // Store the appState in localStorage to persist through the Auth0 redirect
        localStorage.setItem('authRedirectTarget', JSON.stringify(appState));
        
        logger.debug('Stored redirect target in localStorage', {
          targetUrl: to.fullPath
        });
        
        // Redirect to login page
        return next('/login');
      }
      
      // If already heading to login, allow it
      logger.auth('allowing_login_page_access');
      return next();
    }
  } catch (error) {
    logger.error('Auth guard error', error, {
      targetRoute: to.name,
      targetPath: to.path,
      duration: Date.now() - authStart
    });
    
    // On error, redirect to login as a fallback
    return next('/login');
  }
};
